import Phaser from 'phaser';
import { ASSETS_BASE_URL } from '@shared/constants';
import { addGridSpriteSheet } from './gridSpriteSheet';

/**
 * Definición de una animación en el índice de assets
 */
export interface AnimationDefinition {
  name: string;
  type: string; // Clasificación: 'basic', 'effect', 'spell', 'hit', etc.
  columns: number;
  rows: number;
  skipLast: number; // Cuántos frames descontar del final (si la última fila no está completa)
  offsetY?: number; // Offset vertical en píxeles (default 0)
}

/**
 * Genera la key del spritesheet para una animación
 */
export function getAnimationSpriteKey(animationId: number): string {
  return `animation${animationId}`;
}

/**
 * Genera la key de la animación en Phaser
 */
export function getAnimationAnimKey(animationId: number): string {
  return `anim_${animationId}`;
}

/**
 * Precarga las imágenes de animaciones (como imágenes, no spritesheets)
 * El spritesheet se creará después cuando sepamos el tamaño
 */
export function preloadAnimationSprites(
  scene: Phaser.Scene,
  animationIds: number[]
): void {
  for (const id of animationIds) {
    const key = getAnimationSpriteKey(id);
    // Cargar como imagen primero para obtener dimensiones
    scene.load.image(`${key}_img`, `${ASSETS_BASE_URL}/animations/${key}.png`);
  }
}

/**
 * Crea los spritesheets y animaciones en Phaser (sin loop por defecto)
 * Calcula el tamaño de frame automáticamente basado en el tamaño de la imagen
 */
export function createAnimations(
  scene: Phaser.Scene,
  animationIds: number[],
  animationsIndex: Record<string, AnimationDefinition>
): void {
  for (const id of animationIds) {
    const spriteKey = getAnimationSpriteKey(id);
    const animKey = getAnimationAnimKey(id);
    const imgKey = `${spriteKey}_img`;
    const def = animationsIndex[spriteKey];
    if (!def) continue;

    // Obtener la textura de la imagen cargada
    const texture = scene.textures.get(imgKey);
    if (!texture || texture.key === '__MISSING') continue;

    const source = texture.getSourceImage() as HTMLImageElement;

    // Crear spritesheet cortando por bordes redondeados (sin drift si el PNG no es
    // múltiplo exacto de def.columns×def.rows). Frames numerados 0..N-1 row-major,
    // compatibles con generateFrameNumbers más abajo.
    if (!scene.textures.exists(spriteKey)) {
      addGridSpriteSheet(scene, spriteKey, source, def.columns, def.rows);
    }

    // Calcular total de frames
    const totalFrames = def.columns * def.rows - def.skipLast;

    // Crear animación sin loop (el loop se controla al reproducir)
    if (!scene.anims.exists(animKey)) {
      scene.anims.create({
        key: animKey,
        frames: scene.anims.generateFrameNumbers(spriteKey, {
          start: 0,
          end: totalFrames - 1,
        }),
        frameRate: 6,
        repeat: 0, // Sin loop por defecto
      });
    }
  }
}

/**
 * Reproduce una animación sobre un personaje/posición
 * @param scene - La escena de Phaser
 * @param animationId - ID de la animación
 * @param x - Posición X en píxeles
 * @param y - Posición Y en píxeles
 * @param loop - Si debe repetirse indefinidamente
 * @param depth - Profundidad Z (por defecto muy alta para estar sobre el personaje)
 * @returns El sprite de la animación (para poder destruirlo si es loop), o null si no está lista
 */
export function playAnimationAt(
  scene: Phaser.Scene,
  animationId: number,
  x: number,
  y: number,
  loop: boolean = false,
  depth: number = 10000
): Phaser.GameObjects.Sprite | null {
  const spriteKey = getAnimationSpriteKey(animationId);
  const animKey = getAnimationAnimKey(animationId);

  // Verificar que la animación existe antes de crear el sprite
  if (!scene.anims.exists(animKey)) {
    console.warn(`[AnimationSpriteManager] Animation ${animKey} not ready yet`);
    return null;
  }

  const sprite = scene.add.sprite(x, y, spriteKey);
  sprite.setDepth(depth);

  if (loop) {
    sprite.play({ key: animKey, repeat: -1 });
  } else {
    sprite.play(animKey);
    sprite.once('animationcomplete', () => {
      sprite.destroy();
    });
  }

  return sprite;
}

